import { useState, useEffect } from "react";
import { AdminLayout } from "../components/admin/AdminLayout";
import { ShopsTable } from "../components/admin/ShopsTable";
import { ShopDetailsModal } from "../components/admin/ShopDetailsModal";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { useToast } from "../hooks/use-toast";
import { adminApi, type Owner, type OwnersResponse } from "../lib/adminApi";
import {
  Download,
  RefreshCw,
  Filter,
  Store,
  Trash2,
  Loader2,
  AlertTriangle,
  CheckCircle,
  Clock,
  PauseCircle,
} from "lucide-react";

// Imports para el modal de confirmación y dropdowns
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function AdminShops() {
  const [owners, setOwners] = useState<Owner[]>([]);
  const [filteredOwners, setFilteredOwners] = useState<Owner[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOwner, setSelectedOwner] = useState<Owner | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [currentFilter, setCurrentFilter] = useState("all");
  // Mejora 5: Filtro por plan
  const [currentPlan, setCurrentPlan] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const { toast } = useToast();

  // Mejora 2: Estados para ordenamiento
  const [sortColumn, setSortColumn] = useState<string>("createdAt");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  // Estados para eliminación
  const [ownerToDelete, setOwnerToDelete] = useState<Owner | null>(null);
  const [confirmText, setConfirmText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  // Estado para estadísticas reales
  const [realStats, setRealStats] = useState({
    total: 0,
    active: 0,
    trial: 0,
    suspended: 0,
  });

  // Estados de paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // ============ FUNCIÓN PARA CARGAR OWNERS ============
  const fetchOwners = async (page: number = 1) => {
    try {
      setLoading(true);

      console.log("🔍 fetchOwners - currentPlan:", currentPlan);
      console.log(
        '🔍 fetchOwners - currentPlan === "all"?',
        currentPlan === "all",
      );
      console.log(
        "🔍 fetchOwners - enviando plan:",
        currentPlan === "all" ? undefined : currentPlan,
      );

      const result = await adminApi.getOwners({
        status: currentFilter === "all" ? undefined : currentFilter,
        plan: currentPlan === "all" ? undefined : currentPlan, // ← AHORA FUNCIONA
        search: searchQuery || undefined,
        page,
        limit: itemsPerPage,
      });

      console.log(
        "✅ fetchOwners - resultado recibido:",
        result.data.length,
        "owners",
      );

      // Aplicar ordenamiento local
      let sortedData = [...result.data];
      if (sortColumn) {
        sortedData.sort((a, b) => {
          const aVal = a[sortColumn as keyof Owner] || 0;
          const bVal = b[sortColumn as keyof Owner] || 0;

          if (typeof aVal === "string" && typeof bVal === "string") {
            return sortDirection === "asc"
              ? aVal.localeCompare(bVal)
              : bVal.localeCompare(aVal);
          } else {
            const aNum = Number(aVal);
            const bNum = Number(bVal);
            return sortDirection === "asc" ? aNum - bNum : bNum - aNum;
          }
        });
      }

      setOwners(sortedData);
      setFilteredOwners(sortedData);
      setTotalItems(result.total);
      setCurrentPage(result.page);
      setTotalPages(result.totalPages);
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudieron cargar los negocios",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Mejora 2: Manejador de ordenamiento
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    // Refrescar con nuevo ordenamiento
    fetchOwners(currentPage);
  };

  // ============ FUNCIÓN PARA CARGAR ESTADÍSTICAS REALES ============
  const fetchRealStats = async () => {
    try {
      const [allResult, activeResult, trialResult, suspendedResult] =
        await Promise.all([
          adminApi.getOwners({ status: "", page: 1, limit: 1 }),
          adminApi.getOwners({ status: "active", page: 1, limit: 1 }),
          adminApi.getOwners({ status: "trial", page: 1, limit: 1 }),
          adminApi.getOwners({ status: "suspended", page: 1, limit: 1 }),
        ]);

      setRealStats({
        total: allResult.total,
        active: activeResult.total,
        trial: trialResult.total,
        suspended: suspendedResult.total,
      });
    } catch (error) {
      console.error("Error fetching real stats:", error);
    }
  };

  // ============ EFFECTS ============

  // Cargar datos iniciales
  useEffect(() => {
    const loadInitialData = async () => {
      await Promise.all([fetchOwners(1), fetchRealStats()]);
    };
    loadInitialData();
  }, []);

  // Cargar cuando cambia el filtro o plan
  useEffect(() => {
    setCurrentPage(1);
    fetchOwners(1);
  }, [currentFilter, currentPlan]); // Mejora 5: incluir currentPlan

  // Búsqueda con debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setCurrentPage(1);
      fetchOwners(1);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Recargar cuando cambia itemsPerPage u ordenamiento
  useEffect(() => {
    setCurrentPage(1);
    fetchOwners(1);
  }, [itemsPerPage, sortColumn, sortDirection]);

  // ============ FUNCIONES DE MANEJO ============

  const handleViewDetails = (owner: Owner) => {
    setSelectedOwner(owner);
    setDetailsModalOpen(true);
  };

  const handleActivate = async (owner: Owner) => {
    try {
      await adminApi.activateOwner(owner.id, "1_month");
      toast({
        title: "✅ Cuenta activada",
        description: `${owner.businessName} ha sido activado exitosamente`,
      });
      fetchOwners(currentPage);
      fetchRealStats();
    } catch (error) {
      toast({
        title: "❌ Error",
        description: "No se pudo activar la cuenta",
        variant: "destructive",
      });
    }
  };

  const handleSuspend = async (owner: Owner) => {
    try {
      await adminApi.suspendOwner(
        owner.id,
        "Suspensión manual desde panel admin",
      );
      toast({
        title: "⏸️ Cuenta suspendida",
        description: `${owner.businessName} ha sido suspendido`,
      });
      fetchOwners(currentPage);
      fetchRealStats();
    } catch (error) {
      toast({
        title: "❌ Error",
        description: "No se pudo suspender la cuenta",
        variant: "destructive",
      });
    }
  };

  const handleExtendTrial = async (owner: Owner) => {
    try {
      await adminApi.extendOwnerTrial(owner.id, 7);
      toast({
        title: "⏱️ Trial extendido",
        description: `${owner.businessName} tiene 7 días adicionales de trial`,
      });
      fetchOwners(currentPage);
      fetchRealStats();
    } catch (error) {
      toast({
        title: "❌ Error",
        description: "No se pudo extender el trial",
        variant: "destructive",
      });
    }
  };

  const handleDeleteOwner = async () => {
    if (!ownerToDelete || confirmText !== "ELIMINAR") {
      return;
    }

    setIsDeleting(true);
    try {
      await adminApi.deleteOwner(ownerToDelete.id);

      toast({
        title: "✅ Negocio eliminado",
        description: `${ownerToDelete.businessName} ha sido eliminado permanentemente`,
      });

      setOwnerToDelete(null);
      setConfirmText("");

      fetchOwners(currentPage);
      fetchRealStats();
    } catch (error: any) {
      console.error("Error deleting owner:", error);

      let errorMessage = "No se pudo eliminar el negocio";
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast({
        title: "❌ Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setCurrentFilter("all");
    setCurrentPlan("all"); // Mejora 5
    setCurrentPage(1);
  };

  const handleFilterChange = (filter: string) => {
    setCurrentFilter(filter);
    setSearchQuery("");
  };

  // Mejora 5: Manejador para cambio de plan
  const handlePlanChange = (plan: string) => {
    console.log("🎯 handlePlanChange - plan seleccionado:", plan);
    setCurrentPlan(plan);
    setCurrentPage(1);
  };

  const handleRefresh = async () => {
    await Promise.all([fetchOwners(currentPage), fetchRealStats()]);
    toast({
      title: "✅ Datos actualizados",
      description: "Negocios y estadísticas actualizados",
    });
  };

  // Mejora 6: Exportar a CSV
  const handleExportData = () => {
    const headers = [
      "Negocio",
      "Dueño",
      "Email",
      "Plan",
      "Estado",
      "Tiendas",
      "Productos",
      "Ventas",
      "Ingresos (CUP)",
    ];
    const rows = filteredOwners.map((o) => [
      o.businessName,
      o.ownerName,
      o.email,
      o.planType === "basic"
        ? "Básico"
        : o.planType === "professional"
          ? "Profesional"
          : "Enterprise",
      o.accountStatus === "active"
        ? "Activo"
        : o.accountStatus === "trial"
          ? "Trial"
          : "Suspendido",
      o.shopsCount || 1,
      o.totalProducts || 0,
      o.totalSales || 0,
      o.totalRevenue || 0,
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.join(","))
      .join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `negocios_${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      title: "✅ Datos exportados",
      description: `${filteredOwners.length} negocios exportados a CSV`,
    });
  };

  // Calcular estadísticas para el resumen económico (Mejora 3)
  const totalRevenue = owners.reduce(
    (sum, o) => sum + (Number(o.totalRevenue) || 0),
    0,
  );
  const totalProducts = owners.reduce(
    (sum, o) => sum + (Number(o.totalProducts) || 0),
    0,
  );
  const totalSales = owners.reduce(
    (sum, o) => sum + (Number(o.totalSales) || 0),
    0,
  );

  return (
    <AdminLayout title="Gestión de Negocios">
      {/* Mejora 8: Header con estadísticas con gradientes */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-4 bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">
                Total Negocios
              </p>
              <p className="text-3xl font-bold">{realStats.total}</p>
            </div>
            <div className="p-3 bg-white/20 rounded-full backdrop-blur-sm">
              <Store className="h-6 w-6 text-white" />
            </div>
          </div>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-green-500 to-green-600 text-white border-0">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Activos</p>
              <p className="text-3xl font-bold">{realStats.active}</p>
            </div>
            <div className="p-3 bg-white/20 rounded-full backdrop-blur-sm">
              <CheckCircle className="h-6 w-6 text-white" />
            </div>
          </div>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-yellow-500 to-yellow-600 text-white border-0">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-100 text-sm font-medium">En Trial</p>
              <p className="text-3xl font-bold">{realStats.trial}</p>
            </div>
            <div className="p-3 bg-white/20 rounded-full backdrop-blur-sm">
              <Clock className="h-6 w-6 text-white" />
            </div>
          </div>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-red-500 to-red-600 text-white border-0">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-100 text-sm font-medium">Suspendidos</p>
              <p className="text-3xl font-bold">{realStats.suspended}</p>
            </div>
            <div className="p-3 bg-white/20 rounded-full backdrop-blur-sm">
              <PauseCircle className="h-6 w-6 text-white" />
            </div>
          </div>
        </Card>
      </div>

      {/* Mejora 3: Resumen económico */}
      <Card className="p-4 mb-6 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-gray-600">
              Ingresos totales (todos los negocios)
            </p>
            <p className="text-2xl font-bold text-blue-600">
              {totalRevenue.toFixed(2)} CUP
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Productos totales</p>
            <p className="text-2xl font-bold text-green-600">{totalProducts}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Ventas totales</p>
            <p className="text-2xl font-bold text-purple-600">{totalSales}</p>
          </div>
        </div>
      </Card>

      {/* Acciones y filtros */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Todos los Negocios
          </h2>
          <p className="text-gray-600">
            Gestiona y supervisa todos los negocios registrados
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={handleExportData}>
            <Download className="h-4 w-4 mr-2" />
            Exportar
          </Button>
          <Button onClick={handleRefresh} disabled={loading}>
            <RefreshCw
              className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`}
            />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Filtros adicionales (Mejora 5) */}
      <div className="flex flex-wrap gap-2 mb-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <Filter className="h-4 w-4 mr-2" />
              Plan:{" "}
              {currentPlan === "all"
                ? "Todos"
                : currentPlan === "basic"
                  ? "Básico"
                  : currentPlan === "professional"
                    ? "Profesional"
                    : currentPlan === "enterprise"
                      ? "Enterprise"
                      : "Todos"}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={() => handlePlanChange("all")}>
              Todos los planes
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handlePlanChange("basic")}>
              Básico
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handlePlanChange("professional")}>
              Profesional
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handlePlanChange("enterprise")}>
              Enterprise
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Tabla de negocios con ordenamiento */}
      <Card className="p-6">
        <ShopsTable
          owners={filteredOwners}
          loading={loading}
          onViewDetails={handleViewDetails}
          onActivate={handleActivate}
          onSuspend={handleSuspend}
          onExtendTrial={handleExtendTrial}
          onDelete={setOwnerToDelete}
          onSearch={handleSearch}
          onFilterChange={handleFilterChange}
          onClearSearch={handleClearSearch}
          currentFilter={currentFilter}
          searchQuery={searchQuery}
          // Mejora 2: Props de ordenamiento
          onSort={handleSort}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
        />

        {/* Paginación (sin cambios) */}
        {totalPages > 1 && (
          <div className="mt-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <div className="text-sm text-gray-600">
                Mostrando{" "}
                <span className="font-medium">
                  {(currentPage - 1) * itemsPerPage + 1}
                </span>{" "}
                -{" "}
                <span className="font-medium">
                  {Math.min(currentPage * itemsPerPage, totalItems)}
                </span>{" "}
                de <span className="font-medium">{totalItems}</span> negocios
              </div>

              <div className="sm:self-center">
                <select
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm w-full sm:w-auto"
                >
                  <option value="10">10 por página</option>
                  <option value="25">25 por página</option>
                  <option value="50">50 por página</option>
                  <option value="100">100 por página</option>
                </select>
              </div>
            </div>

            <div className="flex flex-col xs:flex-row items-center justify-between gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchOwners(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                className="w-full xs:w-auto order-2 xs:order-1"
              >
                Anterior
              </Button>

              <div className="flex items-center gap-1 order-1 xs:order-2 overflow-x-auto pb-2 xs:pb-0 max-w-full">
                <div className="hidden xs:flex space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <Button
                        key={pageNum}
                        variant={
                          currentPage === pageNum ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => fetchOwners(pageNum)}
                        disabled={loading}
                        className="min-w-[40px]"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>

                <div className="xs:hidden flex items-center gap-1">
                  <Button
                    variant={currentPage === 1 ? "default" : "outline"}
                    size="sm"
                    onClick={() => fetchOwners(1)}
                    disabled={loading}
                  >
                    1
                  </Button>

                  {currentPage > 3 && totalPages > 5 && (
                    <span className="px-2">...</span>
                  )}

                  {currentPage > 2 && currentPage < totalPages - 1 && (
                    <Button
                      variant="default"
                      size="sm"
                      className="min-w-[40px]"
                    >
                      {currentPage}
                    </Button>
                  )}

                  {currentPage < totalPages - 2 && totalPages > 5 && (
                    <span className="px-2">...</span>
                  )}

                  {totalPages > 1 && (
                    <Button
                      variant={
                        currentPage === totalPages ? "default" : "outline"
                      }
                      size="sm"
                      onClick={() => fetchOwners(totalPages)}
                      disabled={loading}
                    >
                      {totalPages}
                    </Button>
                  )}
                </div>

                <div className="xs:hidden text-sm text-gray-600 ml-2 whitespace-nowrap">
                  Pág. {currentPage} de {totalPages}
                </div>
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchOwners(currentPage + 1)}
                disabled={currentPage === totalPages || loading}
                className="w-full xs:w-auto order-3"
              >
                Siguiente
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Modal de detalles */}
      <ShopDetailsModal
        owner={selectedOwner}
        open={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        onActivate={handleActivate}
        onSuspend={handleSuspend}
      />

      {/* Modal de confirmación para eliminar negocio */}
      <AlertDialog
        open={!!ownerToDelete}
        onOpenChange={(open) => !open && setOwnerToDelete(null)}
      >
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Eliminar negocio permanentemente
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <p className="font-medium text-red-800">
                  ⚠️ Esta acción NO se puede deshacer
                </p>
                <p className="text-sm text-red-700 mt-1">
                  Se eliminarán TODAS las tiendas y datos asociados a este
                  negocio:
                </p>
                <ul className="text-sm text-red-700 list-disc list-inside mt-2 space-y-1">
                  <li>Todas las tiendas del negocio</li>
                  <li>Todos los productos del inventario</li>
                  <li>Historial completo de ventas</li>
                  <li>Base de datos de clientes</li>
                  <li>Usuarios y permisos</li>
                  <li>Configuraciones y preferencias</li>
                </ul>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium">
                  Negocio a eliminar:{" "}
                  <span className="font-bold">
                    {ownerToDelete?.businessName}
                  </span>
                </p>
                <p className="text-sm text-gray-600">
                  Dueño: {ownerToDelete?.ownerName} • Email:{" "}
                  {ownerToDelete?.email}
                </p>
                <p className="text-sm text-gray-600">
                  Estado:{" "}
                  <Badge variant="outline">
                    {ownerToDelete?.accountStatus}
                  </Badge>
                </p>
                {ownerToDelete?.shopsCount && ownerToDelete.shopsCount > 1 && (
                  <p className="text-sm font-medium text-amber-600">
                    ⚠️ Este negocio tiene {ownerToDelete.shopsCount} tiendas
                  </p>
                )}
              </div>

              <div className="space-y-2 pt-2">
                <p className="text-sm font-medium">
                  Escribe{" "}
                  <span className="font-bold text-red-600">"ELIMINAR"</span>{" "}
                  para confirmar:
                </p>
                <Input
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                  placeholder="ELIMINAR"
                  className="font-mono text-center tracking-wider"
                  disabled={isDeleting}
                />
                <p className="text-xs text-gray-500">
                  Esta medida de seguridad evita eliminaciones accidentales
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={isDeleting}
              onClick={() => {
                setOwnerToDelete(null);
                setConfirmText("");
              }}
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteOwner}
              disabled={confirmText !== "ELIMINAR" || isDeleting}
              className="bg-red-600 hover:bg-red-700 text-white focus:ring-red-600"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Eliminando...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Eliminar Permanentemente
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
